import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useEmail } from '../../contexts/EmailContext';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import { FiChevronDown, FiEdit, FiMail, FiSend, FiX } from 'react-icons/fi';


const ContactEmailActions = ({ contact, onEmailSent }) => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [emailData, setEmailData] = useState({
    subject: '',
    content: '',
    templateId: ''
  });

  const { getEmailTemplates } = useEmail();
  const templates = getEmailTemplates();

  const handleTemplateSelect = (template) => {
    const personalizedSubject = personalizeText(template.subject, contact);
    const personalizedContent = personalizeText(template.content, contact);
    
    setEmailData({
      subject: personalizedSubject,
      content: personalizedContent,
      templateId: template.id
    });
    setSelectedTemplate(template);
    setShowTemplateMenu(false);
    setShowEmailModal(true);
  };

  const personalizeText = (text, contact) => {
    let personalizedText = text;
    
    const variables = {
      '{{firstName}}': contact.firstName || '',
      '{{lastName}}': contact.lastName || '',
      '{{company}}': contact.company || '',
      '{{title}}': contact.title || '',
      '{{experience}}': contact.experience || '',
      '{{location}}': contact.location || '',
      '{{currentProduction}}': contact.currentProduction || '',
      '{{leadSource}}': contact.leadSource || '',
      '{{senderName}}': 'Sarah Johnson',
      '{{senderTitle}}': 'Regional Director',
      '{{availableDay}}': 'Tuesday',
      '{{alternativeDay}}': 'Thursday',
      '{{timeSlot1}}': 'Tuesday 2:00 PM',
      '{{timeSlot2}}': 'Wednesday 10:00 AM',
      '{{timeSlot3}}': 'Friday 3:00 PM'
    };

    Object.entries(variables).forEach(([variable, value]) => {
      personalizedText = personalizedText.replace(new RegExp(variable.replace(/[{}]/g, '\\$&'), 'g'), value);
    });

    return personalizedText;
  };

  const handleSendEmail = () => {
    // Simulate sending email
    toast.success('Email sent successfully');
    
    // Add to contact's activity timeline
    if (onEmailSent) {
      onEmailSent({
        type: 'email_sent',
        title: 'Email Sent',
        description: emailData.subject,
        timestamp: new Date().toISOString(),
        details: {
          subject: emailData.subject,
          content: emailData.content,
          templateId: emailData.templateId,
          templateName: selectedTemplate?.name
        }
      });
    }

    setShowEmailModal(false);
    setEmailData({ subject: '', content: '', templateId: '' });
    setSelectedTemplate(null);
  };

  const quickEmailTemplates = templates.slice(0, 3);

  return (
    <>
      {/* Quick Email Button with Dropdown */}
      <div className="relative">
        <button
          onClick={() => setShowTemplateMenu(!showTemplateMenu)}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <SafeIcon icon={FiMail} className="text-sm" />
          Send Email
          <SafeIcon icon={FiChevronDown} className="text-sm" />
        </button>

        <AnimatePresence>
          {showTemplateMenu && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="absolute top-full left-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-10"
            >
              <div className="p-3 border-b border-gray-200">
                <h4 className="font-medium text-gray-900">Quick Email Templates</h4>
                <p className="text-xs text-gray-500">Choose a template to send to {contact.firstName}</p>
              </div>
              
              <div className="p-2">
                {quickEmailTemplates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => handleTemplateSelect(template)}
                    className="w-full text-left p-3 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className="font-medium text-gray-900 text-sm">{template.name}</div>
                    <div className="text-xs text-gray-500 mt-1">{template.description}</div>
                    <div className="text-xs text-blue-600 mt-1 font-medium">
                      {template.category.replace('_', ' ')}
                    </div>
                  </button>
                ))}
                
                <button
                  onClick={() => {
                    setShowTemplateMenu(false);
                    setShowEmailModal(true);
                  }}
                  className="w-full text-left p-3 hover:bg-gray-50 rounded-lg transition-colors border-t border-gray-100 mt-2"
                >
                  <div className="flex items-center gap-2 text-blue-600 font-medium text-sm">
                    <SafeIcon icon={FiEdit} className="text-sm" />
                    Compose Custom Email
                  </div>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Email Composer Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Send Email to {contact.firstName} {contact.lastName}
                </h3>
                <p className="text-sm text-gray-600">{contact.email}</p>
                {selectedTemplate && (
                  <p className="text-sm text-blue-600">Template: {selectedTemplate.name}</p>
                )}
              </div>
              <button
                onClick={() => setShowEmailModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiX} className="text-gray-500" />
              </button>
            </div>

            {/* Email Form */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={emailData.subject}
                    onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter email subject"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    value={emailData.content}
                    onChange={(e) => setEmailData(prev => ({ ...prev, content: e.target.value }))}
                    rows={15}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Write your email message..."
                  />
                </div>

                {!selectedTemplate && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">Need a template?</h4>
                    <p className="text-sm text-blue-700 mb-3">
                      Save time by using one of our pre-written recruitment templates.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {quickEmailTemplates.map((template) => (
                        <button
                          key={template.id}
                          onClick={() => handleTemplateSelect(template)}
                          className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded hover:bg-blue-200 transition-colors"
                        >
                          {template.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end items-center gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowEmailModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={!emailData.subject || !emailData.content}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <SafeIcon icon={FiSend} className="text-sm" />
                Send Email
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default ContactEmailActions;